import React, { useState } from 'react';
import {
  CalendarLtr24Regular,
  Checkmark20Regular,
  ContactCard24Regular,
  Edit20Regular,
  Location24Regular,
  Mail24Regular,
  Person24Regular,
  ShieldCheckmark24Regular,
  Tag24Regular,
} from '@fluentui/react-icons';
import { ElicitationField, ElicitationInterrupt, ElicitationResponse } from '@/types/api';
import OrderForm from '@/components/OrderForm';
import PaymentForm from '@/components/PaymentForm';

/* ------------------------------------------------------------------ */
/*  ConfirmationCard — renders an MCP elicitation (the agent is paused, */
/*  waiting for the user). Four looks:                                 */
/*   - card payment (fields carry `x-widget: card-*`) → PaymentForm;   */
/*   - order form (fields carry `x-group`) → OrderForm, step by step;  */
/*   - confirmation (single boolean) → receipt + Confirmer / Modifier;  */
/*   - anything else → a plain generic form (other MCP tools).          */
/* ------------------------------------------------------------------ */

interface ConfirmationCardProps {
  interrupt: ElicitationInterrupt;
  disabled?: boolean;
  onResolve: (responses: Record<string, ElicitationResponse>) => void;
}

type FieldValue = string | number | boolean | null;
type Schema = NonNullable<ElicitationInterrupt['requests'][number]['requested_schema']>;

const isSingleBoolean = (schema?: Schema) => {
  const props = schema?.properties ?? {};
  const keys = Object.keys(props);
  return keys.length === 1 && props[keys[0]].type === 'boolean';
};
const isOrderForm = (schema?: Schema) => Object.values(schema?.properties ?? {}).some((d) => d['x-group']);
const isPaymentForm = (schema?: Schema) =>
  Object.values(schema?.properties ?? {}).some((d) => (d['x-widget'] ?? '').startsWith('card-'));

const LINE_ICONS: [RegExp, React.ReactNode][] = [
  [/^Forfait|^Pass/i, <Tag24Regular />],
  [/^Premier jour|^First day/i, <CalendarLtr24Regular />],
  [/^Skieur|^Skier/i, <Person24Regular />],
  [/^Carte|^Keycard/i, <ContactCard24Regular />],
  [/^Assurance|^Insurance/i, <ShieldCheckmark24Regular />],
  [/^Retrait|^Pick/i, <Location24Regular />],
  [/^Envoi|^E-mail|^Sent/i, <Mail24Regular />],
];
const iconFor = (label: string) => LINE_ICONS.find(([re]) => re.test(label))?.[1] ?? null;

const ConfirmationCard: React.FC<ConfirmationCardProps> = ({ interrupt, disabled, onResolve }) => {
  const [submitted, setSubmitted] = useState<'accept' | 'decline' | null>(null);
  const [values, setValues] = useState<Record<string, Record<string, FieldValue>>>({});

  const single = interrupt.requests.length === 1 ? interrupt.requests[0] : null;

  /* ---- 1. the card payment ---------------------------------------------- */
  if (single && single.mode === 'form' && isPaymentForm(single.requested_schema)) {
    return (
      <PaymentForm
        request={single}
        disabled={disabled}
        onSubmit={(content) => onResolve({ [single.key]: { action: 'accept', content } })}
        onDecline={() => onResolve({ [single.key]: { action: 'decline' } })}
      />
    );
  }

  /* ---- 2. the order form ------------------------------------------------ */
  if (single && single.mode === 'form' && isOrderForm(single.requested_schema)) {
    return (
      <OrderForm
        request={single}
        disabled={disabled}
        onSubmit={(content) => onResolve({ [single.key]: { action: 'accept', content } })}
        onDecline={() => onResolve({ [single.key]: { action: 'decline' } })}
      />
    );
  }

  /* ---- 3. the confirmation receipt ------------------------------------- */
  if (single && isSingleBoolean(single.requested_schema)) {
    const field = Object.keys(single.requested_schema!.properties)[0];
    const lines = (single.message || '').split('\n').map((l) => l.trim()).filter(Boolean);
    const rows = lines.map((line) => {
      const idx = line.indexOf(' : ');
      return idx > 0 ? { label: line.slice(0, idx), value: line.slice(idx + 3) } : { label: '', value: line };
    });
    const totalRow = rows.find((r) => /^Total/i.test(r.label));
    const detailRows = rows.filter((r) => r !== totalRow);
    const accept = () => { setSubmitted('accept'); onResolve({ [single.key]: { action: 'accept', content: { [field]: true } } }); };
    const decline = () => { setSubmitted('decline'); onResolve({ [single.key]: { action: 'decline' } }); };
    return (
      <div className={`receipt${submitted ? ' receipt-done' : ''}`} role="dialog" aria-label="Confirmer votre commande">
        <div className="receipt-head">
          <div className="receipt-kicker">Récapitulatif de votre commande</div>
          {totalRow && (
            <div className="receipt-total">
              <span className="receipt-amount">{totalRow.value.split(' — ')[0]}</span>
              <span className="receipt-note">{totalRow.value.split(' — ')[1] || 'à régler ultérieurement'}</span>
            </div>
          )}
        </div>
        <ul className="receipt-lines">
          {detailRows.map((r, i) => (
            <li key={i} className="receipt-line">
              <span className="receipt-icon">{iconFor(r.label)}</span>
              <span className="receipt-text">
                {r.label && <span className="receipt-label">{r.label}</span>}
                <span className="receipt-value">{r.value}</span>
              </span>
            </li>
          ))}
        </ul>
        {submitted ? (
          <div className="receipt-actions receipt-sent">
            {submitted === 'accept' ? <Checkmark20Regular /> : <Edit20Regular />}
            {submitted === 'accept' ? 'Commande confirmée' : 'Modification demandée'}
          </div>
        ) : (
          <div className="receipt-actions">
            <button type="button" className="of-btn of-btn-ghost" onClick={decline} disabled={disabled}>
              <Edit20Regular /> Modifier
            </button>
            <button type="button" className="of-btn of-btn-primary" onClick={accept} disabled={disabled}>
              <Checkmark20Regular /> Confirmer la commande
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ---- 4. generic fallback (other MCP servers' forms) ------------------ */
  const setField = (reqKey: string, f: string, v: FieldValue) =>
    setValues((s) => ({ ...s, [reqKey]: { ...(s[reqKey] ?? {}), [f]: v } }));
  const missing = interrupt.requests.some((req) => {
    if (req.mode === 'url') return false;
    const required = req.requested_schema?.required ?? [];
    const v = values[req.key] ?? {};
    return required.some((f) => v[f] === undefined || v[f] === null || v[f] === '');
  });
  const accept = () => {
    const responses: Record<string, ElicitationResponse> = {};
    for (const req of interrupt.requests) {
      responses[req.key] = req.mode === 'url' ? { action: 'accept' } : { action: 'accept', content: values[req.key] ?? {} };
    }
    setSubmitted('accept');
    onResolve(responses);
  };
  const decline = () => {
    const responses: Record<string, ElicitationResponse> = {};
    for (const req of interrupt.requests) responses[req.key] = { action: 'decline' };
    setSubmitted('decline');
    onResolve(responses);
  };
  const renderInput = (reqKey: string, f: string, def: ElicitationField) => {
    const v = values[reqKey]?.[f];
    if (def.type === 'boolean') {
      return (
        <label key={f} className="of-checkbox">
          <input type="checkbox" checked={Boolean(v)} onChange={(e) => setField(reqKey, f, e.target.checked)} disabled={disabled} /> {def.title || f}
        </label>
      );
    }
    if (def.enum) {
      return (
        <div key={f} className="of-field of-field-wide">
          <label className="of-label">{def.title || f}</label>
          <select className="of-input" value={(v as string) ?? ''} onChange={(e) => setField(reqKey, f, e.target.value)} disabled={disabled}>
            <option value="">—</option>
            {def.enum.map((o, i) => <option key={o} value={o}>{def.enumNames?.[i] ?? o}</option>)}
          </select>
        </div>
      );
    }
    const numeric = def.type === 'number' || def.type === 'integer';
    return (
      <div key={f} className="of-field of-field-wide">
        <label className="of-label">{def.title || f}</label>
        <input
          className="of-input"
          type={numeric ? 'number' : def.format === 'date' ? 'date' : def.format === 'email' ? 'email' : 'text'}
          value={v === null || v === undefined ? '' : String(v)}
          placeholder={def.description}
          onChange={(e) => setField(reqKey, f, numeric ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
          disabled={disabled}
        />
      </div>
    );
  };
  return (
    <div className={`receipt${submitted ? ' receipt-done' : ''}`} role="dialog">
      <div className="receipt-head"><div className="receipt-kicker">L'assistant a besoin d'une réponse</div></div>
      {interrupt.requests.map((req) => (
        <div key={req.key} className="receipt-body">
          <p className="of-subtitle">{req.message}</p>
          {req.mode === 'url' && req.url && <a href={req.url} target="_blank" rel="noreferrer" className="of-link">Ouvrir le lien</a>}
          {req.mode === 'form' && Object.entries(req.requested_schema?.properties ?? {}).map(([f, d]) => renderInput(req.key, f, d))}
        </div>
      ))}
      {!submitted && (
        <div className="receipt-actions">
          <button type="button" className="of-btn of-btn-ghost" onClick={decline} disabled={disabled}>Annuler</button>
          <button type="button" className="of-btn of-btn-primary" onClick={accept} disabled={disabled || missing}><Checkmark20Regular /> Envoyer</button>
        </div>
      )}
    </div>
  );
};

export default ConfirmationCard;
